import { Point, UnitSystem } from '@/types';
import { distanceBetweenPoints } from '@/core/geometry';

/**
 * Functional core module for scale and unit conversions.
 * All functions are pure (no side effects).
 */

export interface Scale {
    type: 'ratio' | 'calibration';
    ratioNumerator?: number;
    ratioDenominator?: number;
    calibrationPoints?: Point[];
    realWorldDistance?: number;
    /** The unit system `realWorldDistance` was entered in (metres or decimal feet). Absent on calibrations saved
     *  before 2026-10: those read in whatever system they are measured in, as they always did. */
    realWorldUnit?: UnitSystem;
}

const METERS_PER_FOOT = 0.3048;

/** The factor that converts a length in `from`'s base unit (m or ft) to `to`'s; 1 when `from` is unknown. */
export function convertUnit(from: UnitSystem | undefined, to: UnitSystem): number {
    if (!from || from === to) return 1;
    return from === UnitSystem.IMPERIAL ? METERS_PER_FOOT : 1 / METERS_PER_FOOT;
}

/** The regions that measure in feet and inches (Decision 9 of spec--pdfmarkuptool-input-primitives). */
const IMPERIAL_REGIONS = new Set(['US', 'LR', 'MM']);

/**
 * The default unit system for a browser locale (`navigator.language`): imperial only for the United States,
 * Liberia and Myanmar, metric everywhere else. A bare language takes its likely region (`en` -> US). The user
 * can always override it (the landing select, the Scale dialog's unit picker).
 */
/** `defaultUnitSystem` of this browser's language (metric where there is no navigator). */
export function browserDefaultUnitSystem(): UnitSystem {
    return defaultUnitSystem(typeof navigator === 'undefined' ? undefined : navigator.language);
}

export function defaultUnitSystem(locale: string | undefined): UnitSystem {
    if (!locale) return UnitSystem.METRIC;
    let region: string | undefined;
    try {
        const parsed = new Intl.Locale(locale);
        region = parsed.region ?? parsed.maximize().region;
    } catch {
        return UnitSystem.METRIC;
    }
    return region && IMPERIAL_REGIONS.has(region.toUpperCase()) ? UnitSystem.IMPERIAL : UnitSystem.METRIC;
}

// Constants for converting ratio scales to real-world units
// Assumes PDF is printed at 72 DPI (standard PDF resolution)
const POINTS_PER_INCH = 72;
const MM_PER_INCH = 25.4;
const POINTS_PER_MM = POINTS_PER_INCH / MM_PER_INCH; // ~2.835
const POINTS_PER_CM = POINTS_PER_MM * 10; // ~28.35

/**
 * Calculate the scale factor from a scale definition
 * Returns the number of real-world units (meters or feet) per PDF point
 *
 * @param scale - The scale definition
 * @param unitSystem - The project's unit system (metric or imperial)
 */
export function calculateScaleFactor(scale: Scale, unitSystem: UnitSystem = UnitSystem.METRIC): number {
    if (scale.type === 'ratio') {
        if (!scale.ratioNumerator || !scale.ratioDenominator) {
            throw new Error('Ratio scale requires numerator and denominator');
        }

        // Architectural ratio scales assume PDF is printed at 72 DPI
        // Metric (e.g., 1:100): 1cm on paper = 100cm in reality
        // Imperial (e.g., 1:48 or "1/4=1'-0"): 1 inch on paper = 48 inches = 4 feet in reality

        if (unitSystem === UnitSystem.METRIC) {
            // Metric: ratio N:D means D cm in reality per N cm on paper
            // Output in meters, so convert: D cm = D/100 meters
            // N cm on paper = N * POINTS_PER_CM points
            // Scale factor = (D/100 meters) / (N * POINTS_PER_CM points)
            return scale.ratioDenominator / (100 * scale.ratioNumerator * POINTS_PER_CM);
        } else {
            // Imperial: ratio N:D means D inches in reality per N inches on paper
            // Output in feet, so convert: D inches = D/12 feet
            // N inches on paper = N * POINTS_PER_INCH points
            // Scale factor = (D/12 feet) / (N * POINTS_PER_INCH points)
            return scale.ratioDenominator / (12 * scale.ratioNumerator * POINTS_PER_INCH);
        }
    } else if (scale.type === 'calibration') {
        if (!scale.calibrationPoints || scale.calibrationPoints.length !== 2) {
            throw new Error('Calibration scale requires exactly 2 points');
        }
        if (!scale.realWorldDistance) {
            throw new Error('Calibration scale requires real world distance');
        }

        const pageDistance = distanceBetweenPoints(
            scale.calibrationPoints[0],
            scale.calibrationPoints[1]
        );

        if (pageDistance === 0) {
            throw new Error('Calibration points must not be identical');
        }

        return (scale.realWorldDistance / pageDistance) * convertUnit(scale.realWorldUnit, unitSystem);
    }

    throw new Error(`Unknown scale type: ${scale.type}`);
}

/**
 * Human-facing scale label as a ratio. A typed ratio prints exactly ("1:100");
 * a calibration scale prints the EQUIVALENT dimensionless ratio with a leading
 * "~" ("~1:48") -- a calibrated scale is still a ratio, just derived from a
 * measured distance rather than typed. The calibration path inverts the same
 * ratio->factor formula `calculateScaleFactor` uses, so a calibrated 1/4"=1'-0"
 * reads "~1:48" (matching the typed form), not points-per-foot. Returns 'Cal.'
 * only when the scale is malformed.
 */
export function formatScaleRatio(scale: Scale, unitSystem: UnitSystem = UnitSystem.METRIC): string {
    if (scale.type === 'ratio' && scale.ratioNumerator && scale.ratioDenominator) {
        return `${scale.ratioNumerator}:${scale.ratioDenominator}`;
    }
    try {
        const factor = calculateScaleFactor(scale, unitSystem);
        const denom = unitSystem === UnitSystem.METRIC
            ? factor * 100 * POINTS_PER_CM
            : factor * 12 * POINTS_PER_INCH;
        const rounded = denom >= 10 ? Math.round(denom) : Math.round(denom * 10) / 10;
        return `~1:${rounded}`;
    } catch {
        return 'Cal.';
    }
}

/**
 * Convert page distance to real-world distance using scale
 */
export function pageDistanceToRealWorld(pageDistance: number, scale: Scale, unitSystem: UnitSystem = UnitSystem.METRIC): number {
    const scaleFactor = calculateScaleFactor(scale, unitSystem);
    return pageDistance * scaleFactor;
}

/**
 * Convert page area to real-world area using scale
 * Area conversion uses the square of the scale factor
 */
export function pageAreaToRealWorld(pageArea: number, scale: Scale, unitSystem: UnitSystem = UnitSystem.METRIC): number {
    const scaleFactor = calculateScaleFactor(scale, unitSystem);
    return pageArea * scaleFactor * scaleFactor;
}

/**
 * Format distance in the appropriate unit system
 */
export function formatDistance(distance: number, unitSystem: UnitSystem): string {
    // Round to the printed precision FIRST, then split: rounding the remainder after the split prints
    // 2.999 ft as 2' 12.0" and 0.9996 m as 1000 mm.
    if (unitSystem === UnitSystem.METRIC) {
        // Display in meters or millimeters
        const mm = Math.round(distance * 1000);
        if (mm >= 1000) {
            return `${distance.toFixed(2)} m`;
        } else {
            return `${mm} mm`;
        }
    } else {
        // Imperial - convert to feet and inches (tenths of an inch)
        const tenths = Math.round(distance * 120);
        const feet = Math.floor(tenths / 120);
        const inches = (tenths - feet * 120) / 10;

        if (feet > 0) {
            return `${feet}' ${inches.toFixed(1)}"`;
        } else {
            return `${inches.toFixed(1)}"`;
        }
    }
}

/**
 * Format area in the appropriate unit system
 */
export function formatArea(area: number, unitSystem: UnitSystem): string {
    if (unitSystem === UnitSystem.METRIC) {
        // Display in square meters
        return `${area.toFixed(2)} m²`;
    } else {
        // Display in square feet
        return `${area.toFixed(2)} ft²`;
    }
}
